/**
 * Yearly financial view. Workbook equivalents: Summary row 8 (income), row 116 (expenses),
 * row 117 (savings), column P (year totals), Portafoglio Denaro D1/D4 (opening/closing balance).
 */
import { monthsOfYear } from "@/domain/accounting-period";
import type { YearMonth } from "@/domain/model";
import { Decimal, sum, toDecimalString, type DecimalString } from "@/domain/money";
import { computeBalanceSeries, type BalanceRow } from "./balance";
import { categoryBreakdown, natureSplit, type GroupLine, type NatureSplit } from "./budget";
import type { FinanceIndex } from "./data";
import { computeMonth } from "./month";

export interface YearMonthSummary {
  month: YearMonth;
  hasData: boolean;
  income: DecimalString;
  salary: DecimalString;
  extra: DecimalString;
  expenses: DecimalString;
  savings: DecimalString;
  budgetExpenses: DecimalString;
  budgetIncome: DecimalString;
  opening: DecimalString;
  closing: DecimalString;
}

export interface YearReport {
  year: number;
  months: YearMonthSummary[];
  totals: {
    income: DecimalString;
    salary: DecimalString;
    extra: DecimalString;
    expenses: DecimalString;
    /** income − expenses (Summary P117) */
    savings: DecimalString;
    savingsRate: DecimalString | null;
    budgetExpenses: DecimalString;
    budgetIncome: DecimalString;
    /** budget − actual */
    budgetVariance: DecimalString;
    projectedSavings: DecimalString;
  };
  balance: {
    /** Opening balance of January (Portafoglio D1) */
    opening: DecimalString;
    /** Closing balance of December (Portafoglio D4) */
    closing: DecimalString;
    change: DecimalString;
    /** (closing − opening) / opening (Portafoglio E4), null when opening is 0 */
    changeRatio: DecimalString | null;
    anchored: boolean;
    series: BalanceRow[];
  };
  averages: {
    monthsWithData: number;
    /** Over months with data (app default) */
    income: DecimalString | null;
    expenses: DecimalString | null;
    savings: DecimalString | null;
    /** Workbook convention: always divided by 12 (Summary column R) */
    workbookIncome: DecimalString;
    workbookExpenses: DecimalString;
  };
  byGroup: GroupLine[];
  byNature: NatureSplit;
  /** Months with data, highest expenses first */
  highestSpendingMonths: YearMonthSummary[];
}

export function computeYear(index: FinanceIndex, year: number): YearReport {
  const monthKeys = monthsOfYear(year);
  const series = computeBalanceSeries(index, monthKeys[0]!, monthKeys[11]!);
  const months: YearMonthSummary[] = monthKeys.map((m, i) => {
    const r = computeMonth(index, m, { largestCount: 0 });
    const b = series[i]!;
    return {
      month: m,
      hasData: r.hasData,
      income: r.income.total,
      salary: r.income.salary,
      extra: r.income.extra,
      expenses: r.expenses.total,
      savings: r.savings.actual,
      budgetExpenses: r.budget.expenses,
      budgetIncome: r.budget.income,
      opening: b.opening,
      closing: b.closing,
    };
  });

  const total = (pick: (m: YearMonthSummary) => DecimalString) => sum(months.map(pick));
  const income = total((m) => m.income);
  const expenses = total((m) => m.expenses);
  const savings = income.minus(expenses);
  const budgetExpenses = total((m) => m.budgetExpenses);
  const budgetIncome = total((m) => m.budgetIncome);

  const withData = months.filter((m) => m.hasData);
  const avg = (d: Decimal) => (withData.length ? toDecimalString(d.div(withData.length)) : null);

  const opening = new Decimal(series[0]!.opening);
  const closing = new Decimal(series[11]!.closing);

  return {
    year,
    months,
    totals: {
      income: toDecimalString(income),
      salary: toDecimalString(total((m) => m.salary)),
      extra: toDecimalString(total((m) => m.extra)),
      expenses: toDecimalString(expenses),
      savings: toDecimalString(savings),
      savingsRate: income.gt(0) ? toDecimalString(savings.div(income)) : null,
      budgetExpenses: toDecimalString(budgetExpenses),
      budgetIncome: toDecimalString(budgetIncome),
      budgetVariance: toDecimalString(budgetExpenses.minus(expenses)),
      projectedSavings: toDecimalString(budgetIncome.minus(budgetExpenses)),
    },
    balance: {
      opening: toDecimalString(opening),
      closing: toDecimalString(closing),
      change: toDecimalString(closing.minus(opening)),
      changeRatio: opening.isZero() ? null : toDecimalString(closing.minus(opening).div(opening)),
      anchored: series.some((r) => r.anchored),
      series,
    },
    averages: {
      monthsWithData: withData.length,
      income: avg(income),
      expenses: avg(expenses),
      savings: avg(savings),
      workbookIncome: toDecimalString(income.div(12)),
      workbookExpenses: toDecimalString(expenses.div(12)),
    },
    byGroup: categoryBreakdown(index, monthKeys),
    byNature: natureSplit(index, monthKeys),
    highestSpendingMonths: [...withData].sort((a, b) => new Decimal(b.expenses).cmp(a.expenses)),
  };
}
